import { DossierDetailsContent, DossierDetailsHeader, DossierDetailsPanel } from "@/app/pages/dossier/dossier-details-panel";
import { TvvBesteldeRoute, TVVBestelStatus } from "@/entities/tvv";
import { RoleGate, UserRole } from "@/features/auth";
import { useBestelTVVRoutes } from "@/features/tvv/components/regievoerder/bestel-routes-context.ts";
import {
    ActionsDropdown,
    Button,
    DropdownMenuItem,
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyImage,
    EmptyTitle,
} from "@/shared/components/ui";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { RegievoerderRouteItem } from "./regievoerder-route-item";
import { StopRouteDialog } from "./stop-route-dialog";

type BesteloverzichtViewProps = {
    dossierId: string;
};

export function BesteloverzichtView({ dossierId }: Readonly<BesteloverzichtViewProps>) {
    const [searchParams] = useSearchParams();
    const preselectedRouteId = searchParams.get("routeId");
    const { setMode, besteldeRoutes } = useBestelTVVRoutes();
    const [stopDialogRoute, setStopDialogRoute] = useState<TvvBesteldeRoute | null>(null);
    const [stopAllDialogOpen, setStopAllDialogOpen] = useState(false);

    const actieveRoutes = besteldeRoutes?.filter((route) => TvvBesteldeRoute.isNietGestoptOfStopAangevraagd(route)) ?? [];

    if (!besteldeRoutes || besteldeRoutes.length === 0) {
        return (
            <DossierDetailsPanel>
                <DossierDetailsHeader section="Tvv" showBackButton showBerichtBalk />
                <DossierDetailsContent>
                    <Empty>
                        <EmptyHeader>
                            <EmptyImage />
                            <EmptyTitle>Geen Treinvervangend vervoer toegevoegd</EmptyTitle>
                            <EmptyDescription>Er zijn nog geen bussen toegevoegd voor dit dossier.</EmptyDescription>
                        </EmptyHeader>
                        <RoleGate roles={[UserRole.REGIEVOERDER, UserRole.ADMIN]}>
                            <EmptyContent>
                                <Button variant="primary" onClick={() => setMode?.("selecteren")}>
                                    Nieuwe bestelling
                                </Button>
                            </EmptyContent>
                        </RoleGate>
                    </Empty>
                </DossierDetailsContent>
            </DossierDetailsPanel>
        );
    }

    return (
        <DossierDetailsPanel>
            <DossierDetailsHeader section="Tvv" showBackButton showBerichtBalk>
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    <div className="flex items-center justify-end gap-2">
                        <Button size="s" variant="secondary" icon="route" iconSize="xs" onClick={() => setMode?.("selecteren")}>
                            Nieuwe bestelling
                        </Button>
                        <ActionsDropdown iconSize="xs">
                            <DropdownMenuItem onClick={() => setStopAllDialogOpen(true)} disabled={actieveRoutes.length === 0}>
                                Alle routes stoppen
                            </DropdownMenuItem>
                        </ActionsDropdown>
                    </div>
                </RoleGate>
            </DossierDetailsHeader>
            <DossierDetailsContent className="space-y-4">
                <ul className="flex flex-col" role="list">
                    {besteldeRoutes.map((route) => {
                        const routeKey = `${route.stationCodes.join("-")}-${route.status}`;
                        const hasRouteIdQueryParam = !!preselectedRouteId;
                        const defaultOpen = hasRouteIdQueryParam
                            ? preselectedRouteId === route.id.toString()
                            : route.status !== TVVBestelStatus.Gestopt;
                        return (
                            <RegievoerderRouteItem
                                key={routeKey}
                                dossierId={dossierId}
                                route={route}
                                defaultOpen={defaultOpen}
                                onStopRoute={() => setStopDialogRoute(route)}
                            />
                        );
                    })}
                </ul>

                {stopDialogRoute && (
                    <StopRouteDialog
                        open={!!stopDialogRoute}
                        onOpenChange={(open) => !open && setStopDialogRoute(null)}
                        dossierId={dossierId}
                        routes={[stopDialogRoute]}
                    />
                )}

                <StopRouteDialog
                    open={stopAllDialogOpen}
                    onOpenChange={setStopAllDialogOpen}
                    dossierId={dossierId}
                    routes={actieveRoutes}
                />
            </DossierDetailsContent>
        </DossierDetailsPanel>
    );
}
